"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import "swiper/css";
import "swiper/css/navigation";
import { useEffect, useRef } from "react";
import { loadGsap, shouldReduceMotion } from "@/lib/gsap";
import Bg_Section from "~/public/assets/backgrounds/bg-section-bottom.png";
import IconCheckCircle from "~/public/assets/services/icon-check-circle.png";
import type { ServicePageContent } from "../../../types/types";
type ResultsServicesSectionProps = {
  service: ServicePageContent;
};
export default function ResultsServicesSection(t0) {
  const $ = _c(25);
  const {
    service
  } = t0;
  const results = service.results;
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const firstGroupRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== results) {
    t1 = () => {
      if (!results || shouldReduceMotion()) {
        return;
      }
      let cleanup;
      let resizeObserver;
      let cancelled = false;
      const setupAnimation = async function setupAnimation() {
        const track = trackRef.current;
        const firstGroup = firstGroupRef.current;
        if (!track || !firstGroup) {
          return;
        }
        const gsap = await loadGsap();
        if (cancelled) {
          return;
        }
        const ctx = gsap.context(() => {
          let tween;
          const startLoop = () => {
            tween?.kill();
            gsap.set(track, {
              x: 0
            });
            const styles = window.getComputedStyle(track);
            const gap = Number.parseFloat(styles.columnGap || styles.gap || "0");
            const loopDistance = firstGroup.offsetWidth + gap;
            if (!loopDistance) {
              return;
            }
            tween = gsap.to(track, {
              x: -loopDistance,
              duration: 28,
              ease: "none",
              repeat: -1
            });
          };
          startLoop();
          resizeObserver = new ResizeObserver(() => {
            startLoop();
          });
          resizeObserver.observe(track);
          resizeObserver.observe(firstGroup);
        }, sectionRef);
        cleanup = () => {
          resizeObserver?.disconnect();
          ctx.revert();
        };
      };
      setupAnimation();
      return () => {
        cancelled = true;
        cleanup?.();
      };
    };
    t2 = [results];
    $[0] = results;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (!results) {
    return null;
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Image src={Bg_Section} alt="Background decorativo" className="pointer-events-none absolute inset-x-0 bottom-0 h-full w-full object-cover object-bottom scale-x-[-1] opacity-90" priority={false} sizes="100vw" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== results.tag) {
    t4 = <p className="tag">{results.tag}</p>;
    $[4] = results.tag;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== results.title) {
    t5 = <h2 className="title">{results.title}</h2>;
    $[6] = results.title;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = <div className="relative z-10 max-w-120">{t4}{t5}</div>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== results.cards) {
    t7 = results.cards.map(_temp);
    $[11] = results.cards;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = <div ref={firstGroupRef} className="flex shrink-0 gap-4">{t7}</div>;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== results.cards) {
    t9 = results.cards.map(_temp2);
    $[15] = results.cards;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t9) {
    t10 = <div className="flex shrink-0 gap-4" aria-hidden="true">{t9}</div>;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t8) {
    t11 = <div ref={trackRef} className="mt-16 relative flex w-max gap-4 px-7 will-change-transform">{t8}{t10}</div>;
    $[19] = t10;
    $[20] = t8;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11 || $[23] !== t6) {
    t12 = <section className="relative bg-gray-1100 text-white">{t3}<div className="md:container-padding"><div className="container relative md:border-x md:border-gray-300 px-6 pt-18 lg:px-18 lg:pt-28 3xl:px-24! pb-28">{t6}{t11}</div></div></section>;
    $[22] = t11;
    $[23] = t6;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
}
function _temp2(card_0) {
  return <article key={`${card_0.title}-duplicate`} className="flex items-center gap-4 rounded-2xl bg-gray-1000 p-5 md:p-9 whitespace-nowrap"><Image src={IconCheckCircle} alt="" aria-hidden="true" className="h-9 w-9 shrink-0" width={24} height={24} /><h3 className="text-white/70">{card_0.title}</h3></article>;
}
function _temp(card) {
  return <article key={card.title} className="flex items-center gap-4 rounded-2xl bg-gray-1000 p-5 md:p-9 whitespace-nowrap"><Image src={IconCheckCircle} alt="" aria-hidden="true" className="h-9 w-9 shrink-0" width={24} height={24} /><h3 className="text-white/70">{card.title}</h3></article>;
}